// Editor.jsx
// Standalone ProseMirror editor component with optional collaboration

import React, {useCallback, useMemo, useRef, useState, lazy, Suspense} from "react";
import {createPortal} from 'react-dom';
import Box from '@mui/material/Box';

import {useEditorSetup} from "./hooks/useEditorSetup";
import {useWikidataEntity} from "./hooks/useWikidataEntity";
import {useEntitySearchEvent} from "./hooks/useEntitySearchEvent";

import EditorContent from "./components/EditorContent";
import EditorLoadingOverlay from "./components/EditorLoadingOverlay";
import ClassificationMenu from './components/ClassificationMenu';
import WikidataDialogs from "./components/WikidataDialogs";

import "prosemirror-view/style/prosemirror.css";
import "./styles/editor.css";

const AnnotationEditorPortal = lazy(() => import('../figure-annotation/AnnotationEditorPortal'));

const EDITOR_BOX_SX = {
    position: 'relative',
    height: '100%',
    border: '1px solid transparent',
    borderRadius: '8px',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
};

export default function Editor(props) {
    const {
        documentId = null,
        user = null,
        websocketUrl = process.env.REACT_APP_WEBSOCKET_URL || 'wss://awp.minsky.ai/ws/collab',
        showDocumentHeader = true,
        showToolbar = true,
        className = '',
        onReady = null,
        onChange = null,
        onHeaderChange = null,
        documentHeader = null,
        marginRail = null,
        citations = null,
        snippetDrop = null,
        onSelectionChange = null,
        editorMode = 'full',
        readOnly = false,
        seedPmJson = null,
    } = props;

    const isBasic = editorMode === 'basic';
    const effectiveShowToolbar = showToolbar && !readOnly;
    const editorClassName = [className, isBasic ? 'pm-editor--basic' : ''].filter(Boolean).join(' ');

    const editorRef = useRef(null);
    const [editorMountTick, setEditorMountTick] = useState(0);
    const handleEditorMountRef = useCallback((node) => {
        editorRef.current = node;
        if (node) {
            setEditorMountTick((t) => t + 1);
        }
    }, []);

    // Wikidata entity management (disabled in basic mode)
    const {
        searchDialog,
        openEntitySearch,
        closeEntitySearch,
        handleEntitySelect,
    } = useWikidataEntity();

    // Listen for global entity search events from plugins
    useEntitySearchEvent(isBasic ? () => {} : openEntitySearch);

    const {
        viewRef,
        viewTick,
        isLoading,
        loadingStage,
        providerRef,
        collabMaps,
        headerMetaTick,
        classificationMenu
    } = useEditorSetup({
        documentId,
        user,
        websocketUrl,
        editorMountRef: editorRef,
        editorMountTick,
        onReady,
        onChange,
        snippetDrop: isBasic ? null : snippetDrop,
        inlineInteractionPopovers: !isBasic,
        onSelectionChange,
        editorMode,
        readOnly,
        seedPmJson,
    });

    const menuPortal = useMemo(() => createPortal(
        <ClassificationMenu
            anchorEl={classificationMenu?.anchorEl}
            anchorPosition={classificationMenu?.anchorPosition}
            open={Boolean(classificationMenu?.open)}
            onClose={classificationMenu?.closeMenu}
            value={classificationMenu?.getValue?.()}
            onApply={classificationMenu?.handleApply}
        />,
        document.body
    ), [classificationMenu]);


    return (
        <Box
            className={`pm-editor ${editorClassName}`}
            sx={EDITOR_BOX_SX}
        >
            <EditorContent
                showDocumentHeader={showDocumentHeader}
                showToolbar={effectiveShowToolbar}
                documentId={documentId}
                onHeaderChange={onHeaderChange}
                documentHeader={documentHeader}
                view={viewRef.current}
                provider={providerRef.current}
                viewTick={viewTick}
                editorRef={handleEditorMountRef}
                marginRail={isBasic ? null : marginRail}
                citations={citations}
                yDocMeta={collabMaps?.yDocMeta}
                ydoc={collabMaps?.ydoc}
                headerMetaTick={headerMetaTick}
                collaboration={!!documentId}
                editorMode={editorMode}
            />

            <EditorLoadingOverlay
                isLoading={isLoading}
                loadingStage={loadingStage}
            />

            {menuPortal}

            {/* Wikidata dialogs */}
            {!isBasic && (
                <WikidataDialogs
                    entityDialog={{open: false}}
                    searchDialog={searchDialog}
                    onEntityDialogClose={() => {}}
                    onEntitySelect={handleEntitySelect}
                    onSearchClose={closeEntitySearch}
                />
            )}

            <Suspense fallback={null}>
                <AnnotationEditorPortal />
            </Suspense>
        </Box>
    );
}
